/**
 * Service layer — Client Hub domain (Round 9).
 *
 * Two audiences, one file:
 *   - PUBLIC (client, no Clerk): getJourney(token), respond(token, …)
 *   - TEAM (Clerk-authed): admin authoring actions via /api/journey-admin
 *
 * Backed by Supabase (client_journeys / journey_entries / journey_responses)
 * through the api/journey*.js serverless functions. Components never call the
 * endpoints directly — everything routes through here (data-access rule).
 */

/* ── Public client reads/writes (token is the only credential) ───────────── */

/*
 * PREVIEW AS A RECIPIENT (docs/CLIENT_HUB_PREVIEW.md). A team member previewing the Hub through one
 * recipient's eyes holds a preview PASS ('pv1.…', minted by previewAsRecipient below) in place of
 * that person's token. The pass itself is the preview flag: every READ here sends it to the same
 * endpoint the client's own link uses, and every WRITE / notification checks isPreviewToken first
 * and answers locally instead — nothing is saved, emailed, pinged or uploaded. The server refuses a
 * pass on every write route as well (api/_shareDocs.js#resolveJourneyAccess), so a path missed here
 * still cannot write. A pass also never carries the team member's own Clerk session along: the
 * server must see the client, not staff.
 */
export function isPreviewToken(token) {
  return typeof token === 'string' && token.startsWith('pv1.')
}

let _previewSeq = 0
/** A stand-in for a row the server would have returned, for a write made in a preview. */
export function previewRow(fields) {
  return { id: 'preview-' + Date.now() + '-' + (++_previewSeq), created_at: new Date().toISOString(), _preview: true, ...fields }
}

// PIN-proof cache (see database/133_client_journey_access.sql + api/journey-pin.js). Once a
// journey's PIN is verified, the server issues a signed, time-limited proof instead of making
// the client resend the raw PIN on every call — cached per-token so a returning client on the
// same device/browser isn't re-prompted. Purely a convenience cache: every server endpoint that
// checks it re-validates the signature/expiry itself, so a tampered localStorage value just fails
// closed (back to a PIN prompt), never open.
function pinProofKey(token) { return 'journey-pin-proof:' + token }
export function getCachedPinProof(token) {
  try { return localStorage.getItem(pinProofKey(token)) || null } catch { return null }
}
export function setCachedPinProof(token, proof) {
  try { if (proof) localStorage.setItem(pinProofKey(token), proof); else localStorage.removeItem(pinProofKey(token)) } catch { /* ignore */ }
}

// A team member's OWN Clerk session, forwarded so the otherwise-public journey endpoints can
// recognize "this is staff" (resolveStaffView, api/_shareDocs.js) and skip the PIN gate, the
// view-audit log, and any write. Resolves to null for a real client (no session) or if this page
// never set window.__clerkGetToken (mirrors SketchPage.jsx's own pattern for a standalone route) —
// always safe to call unconditionally. Exported so MilestoneEntry.jsx's own direct fetch (the
// drawings-PDF compile call) can attach it too, not just the calls that go through this file.
export async function getOptionalStaffToken(journeyToken) {
  if (isPreviewToken(journeyToken)) return null   // a preview is seen as the client, never as staff
  if (typeof window.__clerkGetToken !== 'function') return null
  try { return (await window.__clerkGetToken()) || null } catch { return null }
}

async function staffAuthHeader(journeyToken) {
  const t = await getOptionalStaffToken(journeyToken)
  return t ? { Authorization: 'Bearer ' + t } : {}
}

/**
 * Verify a journey's PIN. Returns the signed proof on success (caller should cache it via
 * setCachedPinProof). Throws an Error with .code = 'pin_invalid' | 'locked' | other on failure;
 * a 'locked' error carries .retryAt.
 */
export async function verifyJourneyPin(token, pin) {
  if (isPreviewToken(token)) return null   // a preview never meets the PIN gate
  const r = await fetch('/api/journey-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: token, pin }),
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) {
    const e = new Error(j.error || ('PIN check failed: ' + r.status))
    e.code = j.error || 'error'
    if (j.retryAt) e.retryAt = j.retryAt
    throw e
  }
  return j.proof
}

/**
 * Fetch a journey by its magic-link token. Returns { project, entries, responses, isStaffView }.
 * Throws a typed Error (.code) on: 'not_found', 'pin_required', 'pin_invalid', 'locked' (with
 * .retryAt) — callers show a PIN prompt for the pin_* codes rather than a generic error state.
 */
export async function getJourney(token) {
  const proof = getCachedPinProof(token)
  const qs = new URLSearchParams({ t: token })
  if (proof) qs.set('pinProof', proof)
  const r = await fetch('/api/journey?' + qs.toString(), { headers: await staffAuthHeader(token) })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) {
    const e = new Error(j.error || ('Client Hub request failed: ' + r.status))
    e.code = j.error || (r.status === 404 ? 'not_found' : 'error')
    if (j.retryAt) e.retryAt = j.retryAt
    // A stale/expired cached proof the server no longer honors — clear it so the next attempt
    // (after a fresh PIN entry) starts clean instead of looping on the same bad value.
    if (e.code === 'pin_invalid') setCachedPinProof(token, null)
    throw e
  }
  // A fresh proof rides along on every successful response once the gate is passed — recache it
  // so the client's copy never silently falls behind the server's TTL.
  if (j.pinProof) setCachedPinProof(token, j.pinProof)
  return j
}

/**
 * Fetch the live branded HTML web view of a posted proposal (token is the only credential).
 * Returns the self-contained HTML document string (rendered into a sandboxed iframe via srcDoc).
 * Throws on any non-200 — callers fall back to the PDF-based review flow.
 */
export async function getProposalHtml(token, proposalId) {
  const proof = getCachedPinProof(token)
  const qs = new URLSearchParams({ t: token, p: proposalId })
  if (proof) qs.set('pinProof', proof)
  const r = await fetch('/api/journey-proposal-html?' + qs.toString(), { headers: await staffAuthHeader(token) })
  if (!r.ok) throw new Error('Proposal view failed: ' + r.status)
  return r.text()
}

/**
 * Best-effort Slack ping to the project's channel — fired every time a client opens the
 * "View proposal" viewer. No response is recorded server-side; this is purely a team
 * notification side-channel, kept separate from journey_responses so opening the document never
 * marks the entry "Client responded" in the internal author view. Never throws — a failed ping
 * must not block the client from viewing their proposal.
 */
export async function notifyProposalViewed(token, proposalType) {
  if (isPreviewToken(token)) return   // preview: nobody is told
  try {
    await fetch('/api/journey-proposal-viewed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
      body: JSON.stringify({ t: token, proposalType, pinProof: getCachedPinProof(token) }),
    })
  } catch (_) { /* best-effort */ }
}

/** Same endpoint as notifyProposalViewed, `event: 'downloaded'` distinguishes a "Download PDF"
 * click from opening the web viewer — both are engagement signals biz-dev wants visibility into. */
export async function notifyProposalDownloaded(token, proposalType) {
  if (isPreviewToken(token)) return   // preview: nobody is told
  try {
    await fetch('/api/journey-proposal-viewed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
      body: JSON.stringify({ t: token, proposalType, event: 'downloaded', pinProof: getCachedPinProof(token) }),
    })
  } catch (_) { /* best-effort */ }
}

/** Same endpoint as notifyProposalViewed/notifyProposalDownloaded, `docKind: 'reference'` covers
 * the References letter (no signing step — opening/downloading IS the whole engagement signal). */
export async function notifyReferenceViewed(token) {
  if (isPreviewToken(token)) return   // preview: nobody is told
  try {
    await fetch('/api/journey-proposal-viewed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
      body: JSON.stringify({ t: token, docKind: 'reference', pinProof: getCachedPinProof(token) }),
    })
  } catch (_) { /* best-effort */ }
}
export async function notifyReferenceDownloaded(token) {
  if (isPreviewToken(token)) return   // preview: nobody is told
  try {
    await fetch('/api/journey-proposal-viewed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
      body: JSON.stringify({ t: token, docKind: 'reference', event: 'downloaded', pinProof: getCachedPinProof(token) }),
    })
  } catch (_) { /* best-effort */ }
}

/**
 * Best-effort Slack ping to the PROJECT's own channel — fired every time a client opens a
 * presentation-type journey entry (Conceptual Framework, Integrated Design Proposal, etc.) via
 * "View presentation". Unlike notifyProposalViewed (fixed #aj_biz-dev channel — no project exists
 * yet at that stage), a presentation milestone always belongs to a real project with its own
 * channel. Fires on every open, no dedup — never throws, must not block the client's view.
 */
export async function notifyPresentationViewed(token, presentationName) {
  if (isPreviewToken(token)) return   // preview: nobody is told
  try {
    await fetch('/api/journey-presentation-viewed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
      body: JSON.stringify({ t: token, presentationName, pinProof: getCachedPinProof(token) }),
    })
  } catch (_) { /* best-effort */ }
}

/**
 * Best-effort Slack ping to the PROJECT's own channel when a client is looking at a Sketch ribbon
 * entry: "Name is looking at the <ribbon> sketch ribbon we posted on <date>." Same endpoint as
 * notifyPresentationViewed (its `entryId` branch); the server reads the ribbon's name and posted
 * date itself. Once per visit per ribbon — the Hub's carousel re-shows a card every time the
 * client swipes back to it, and that should not re-ping the channel. Never throws.
 */
const _sketchRibbonPinged = new Set()
export async function notifySketchRibbonViewed(token, entryId) {
  if (!token || !entryId || isPreviewToken(token)) return
  const key = 'aj-sketch-ribbon-seen:' + entryId
  if (_sketchRibbonPinged.has(key)) return
  _sketchRibbonPinged.add(key)
  try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1') } catch (_) { /* storage blocked — the in-memory guard still holds for this page */ }
  try {
    await fetch('/api/journey-presentation-viewed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
      body: JSON.stringify({ t: token, entryId, pinProof: getCachedPinProof(token) }),
    })
  } catch (_) { /* best-effort */ }
}

/** Subscribe a client email to this journey's update notifications (token is the only credential). */
export async function subscribeToJourney(token, email) {
  if (isPreviewToken(token)) return { ok: true, preview: true }   // preview: no subscription is saved
  const r = await fetch('/api/journey-subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
    body: JSON.stringify({ t: token, email, pinProof: getCachedPinProof(token) }),
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(j.error || ('Subscribe failed: ' + r.status))
  return j
}

/**
 * Tick one step of the Client Hub "Getting started" tour (docs/CLIENT_HUB_TOUR.md). Returns the tour
 * as the server now has it, or null when nothing was recorded server-side: a preview (nothing is
 * saved), a legacy shared link with no recipient, or a team member's own session. The caller then
 * keeps the tick itself. Never throws: a missed tick is retried next time the step is finished.
 */
export async function saveTourStep(token, step, skipped = false) {
  if (isPreviewToken(token)) return null
  try {
    const r = await fetch('/api/journey-tour', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
      body: JSON.stringify({ t: token, step, skipped, pinProof: getCachedPinProof(token) }),
    })
    const j = await r.json().catch(() => ({}))
    return (r.ok && j.recorded && j.tour) ? j.tour : null
  } catch { return null }
}

/** Record a client response to an entry. kind: acknowledge|homework_done|approve|request_changes|comment */
export async function respond(token, entryId, kind, payload = {}) {
  // Preview: the response exists only in the page. Callers that keep their own list of responses
  // (useJourney) add it themselves; this is what the server would have handed back.
  if (isPreviewToken(token)) return { response: previewRow({ entry_id: entryId, kind, payload }) }
  const r = await fetch('/api/journey-respond', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
    body: JSON.stringify({ t: token, entryId, kind, payload, pinProof: getCachedPinProof(token) }),
  })
  if (!r.ok) throw new Error('Respond failed: ' + r.status)
  return r.json()
}

/**
 * A site survey / as-built proposal request from the Client Hub card (docs/OWNER_PROVIDER_REQUESTS.md):
 * payload { providerIds, own: [{ name, email }], ack: true, titleReport, access, occupied, workPlanned }.
 * Sent in the owner's name to each provider. Returns { requests, sent, failed } or throws with the
 * server's reason (no_owner_email, ack_required, nobody_to_send, too_many, no_address).
 * A preview sends nothing and pretends each pick went out.
 */
export async function sendProviderRequest(token, entryId, payload, providers = []) {
  if (isPreviewToken(token)) {
    const names = [...(payload.providerIds || []).map(id => (providers.find(p => p.id === id) || {}).name), ...(payload.own || []).map(o => o.name)].filter(Boolean)
    return { requests: names.map(name => ({ name, status: 'sent', sentAt: new Date().toISOString(), sentBy: 'Preview' })), sent: names, failed: [], preview: true }
  }
  const r = await fetch('/api/journey-respond', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
    body: JSON.stringify({ t: token, entryId, kind: 'provider_request', payload, pinProof: getCachedPinProof(token) }),
  })
  const d = await r.json().catch(() => ({}))
  if (!r.ok) { const e = new Error(d.error || ('Send failed: ' + r.status)); e.code = d.error; throw e }
  return d
}

/**
 * One schedule item on the Selections Package card, answered (2026-10-26): payload
 * { itemId, version, label, scheduleType, status: 'approved'|'not_approved'|'discuss', note }.
 * Returns { decision: { schedule_item_id, status, note, decided_by_name, decided_at } }.
 */
export async function decideSelectionItem(token, entryId, payload) {
  if (isPreviewToken(token)) return { decision: { schedule_item_id: payload.itemId, status: payload.status, note: payload.note || null, decided_by_name: 'Preview', decided_at: new Date().toISOString() } }
  const r = await fetch('/api/journey-respond', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
    body: JSON.stringify({ t: token, entryId, kind: 'selection_item', payload, pinProof: getCachedPinProof(token) }),
  })
  if (!r.ok) throw new Error('Saving that answer failed: ' + r.status)
  return r.json()
}

/**
 * Save-in-progress for the onboarding questionnaire (database/141) — best-effort, silent on
 * failure by design: a dropped autosave shouldn't interrupt someone mid-questionnaire with an
 * error about a background save they didn't ask for. Rejects only on a genuine network/HTTP
 * failure so callers can choose to ignore it (they do — see OnboardingQuestionnaireEntry.jsx).
 */
export async function saveQuestionnaireProgress(token, entryId, answers, page) {
  if (isPreviewToken(token)) return { ok: true, preview: true }
  const r = await fetch('/api/journey-questionnaire-progress', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: token, entryId, answers, page, pinProof: getCachedPinProof(token) }),
  })
  if (!r.ok) throw new Error('Save progress failed: ' + r.status)
  return r.json()
}

/* ── Team authoring (Clerk-authed) ───────────────────────────────────────── */

async function adminPost(action, body) {
  const token = typeof window.__clerkGetToken === 'function'
    ? await window.__clerkGetToken().catch(() => null)
    : null
  const r = await fetch('/api/journey-admin', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
    },
    body: JSON.stringify({ action, ...body }),
  })
  if (!r.ok) throw new Error('Client Hub admin (' + action + ') failed: ' + r.status)
  return r.json()
}

/** Read-only: the project's journey if one already exists. Returns { journey: null, link: null } if not — never creates one. */
export function getLink(projectId) {
  return adminPost('getLink', { projectId })
}

/** Get the project's journey (creating its link if absent). Returns { journey, link }. */
export function getOrCreateLink(projectId, projectName) {
  return adminPost('getOrCreateLink', { projectId, projectName })
}

/** List all entries (published + drafts) for a journey. */
export function listEntries(journeyId) {
  return adminPost('listEntries', { journeyId })
}

/**
 * Create or update an entry.
 * entry: { id?, journeyId, type, title, data,
 *          milestoneAirtableId?, milestoneType?, milestoneTypeId?, meetingAirtableId?,
 *          position?, published? }
 * milestoneType/milestoneTypeId are both frozen SNAPSHOTS at post time (Name + stable id of the
 * deliverable type) — never re-derive them from a later Airtable read.
 */
export function upsertEntry(entry) {
  return adminPost('upsertEntry', { entry })
}

/** The project's AlderCAD models for a "Model walkthrough" entry:
 *  { models: [{ modelId, conceptId, conceptName, selected, savedAt }] } (selected concept first). */
export function getWalkthroughModels(projectId) {
  return adminPost('walkthroughModels', { projectId })
}

/** Reorder entries. orderedIds: array of entry ids in the desired order. */
export function reorderEntries(journeyId, orderedIds) {
  return adminPost('reorderEntries', { journeyId, orderedIds })
}

/** Delete an entry. */
export function deleteEntry(entryId) {
  return adminPost('deleteEntry', { entryId })
}

/** Journey entries tied to a milestone (no journey is created if absent). Returns an array. */
export function getJourneyEntriesForMilestone(projectId, milestoneId) {
  return adminPost('entriesForMilestone', { projectId, milestoneId }).then(d => d.entries || [])
}

/**
 * The Client Hub entries filed under a milestone's Documents: sketch ribbons (Matt, 2026-10-26) and
 * PDF documents (2026-10-29, docs/CLIENT_HUB_PDF_DOCUMENTS.md). Each stays a plain entry placed by
 * phase + date; the milestone it belongs to is data.milestoneId — NOT milestone_airtable_id, which
 * would make it a milestone-backed entry (posted-only, and deleted on unpost). Never creates a
 * journey. Returns { journeyId, entries }.
 */
export const MILESTONE_FILED_HUB_TYPES = ['sketches', 'document']
export async function getHubEntriesForMilestone(projectId, milestoneId) {
  const { journey } = await getLink(projectId)
  if (!journey) return { journeyId: null, entries: [] }
  const { entries } = await listEntries(journey.id)
  return {
    journeyId: journey.id,
    entries: (entries || []).filter(e => MILESTONE_FILED_HUB_TYPES.includes(e.type) && e.data && e.data.milestoneId === milestoneId),
  }
}

/**
 * Upload a PDF for a "PDF document" Hub entry (docs/CLIENT_HUB_PDF_DOCUMENTS.md). Straight to
 * Storage through signed URLs (decks run 30 MB+, past the function body limit). Then every page is
 * drawn here, once, into a JPEG stored beside the PDF, so the Hub shows small images and never
 * opens the PDF to view it (the Needles deck: 34 MB of PDF, a few hundred KB a page as images).
 * onProgress(done, total) reports the pages. Returns the entry's data.pdf object.
 */
const HUB_PAGE_WIDTH = 2000
async function openPdf(src) {
  const { loadPdfjs } = await import('../lib/pdfjs.js')
  const pdfjs = await loadPdfjs()
  if (!pdfjs) throw new Error('The PDF reader did not load.')
  return pdfjs.getDocument(src).promise
}
async function pageShape(pdf) {
  const vp = (await pdf.getPage(1)).getViewport({ scale: 1 })
  return Math.round((vp.width / vp.height) * 1000) / 1000
}
// Draw every page and upload it to the signed URLs the server handed out. All or nothing: a
// document with only some page images falls back to drawing the PDF, so a failure returns null.
async function drawAndUploadPages(pdf, pages, onProgress) {
  const images = []
  for (let i = 0; i < pages.length; i++) {
    const page = await pdf.getPage(i + 1)
    const base = page.getViewport({ scale: 1 })
    const longEdge = Math.max(base.width, base.height)
    const vp = page.getViewport({ scale: HUB_PAGE_WIDTH / longEdge })
    const canvas = document.createElement('canvas')
    canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height)
    const cctx = canvas.getContext('2d')
    cctx.fillStyle = '#fff'; cctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: cctx, viewport: vp }).promise
    const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.82))
    if (!blob) return null
    const put = await fetch(pages[i].signedUrl, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg', 'x-upsert': 'true' }, body: blob })
    if (!put.ok) return null
    images.push({ url: pages[i].publicUrl, path: pages[i].path, w: canvas.width, h: canvas.height, bytes: blob.size })
    canvas.width = 0; canvas.height = 0
    if (onProgress) onProgress(i + 1, pages.length)
  }
  return images
}
export async function uploadHubDocument(journeyId, file, onProgress) {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let pdf = null, pageCount = 0, aspect = null
  try { pdf = await openPdf({ data: bytes.slice() }); pageCount = pdf.numPages; aspect = await pageShape(pdf) }
  catch (e) { console.error('[journey] could not read the PDF', e) }
  const up = await adminPost('documentUploadUrl', { journeyId, filename: file.name, pageCount })
  const put = await fetch(up.signedUrl, { method: 'PUT', headers: { 'Content-Type': 'application/pdf', 'x-upsert': 'true' }, body: file })
  if (!put.ok) throw new Error('Upload failed (' + put.status + ')')
  let images = null
  if (pdf && up.pages && up.pages.length) {
    try { images = await drawAndUploadPages(pdf, up.pages, onProgress) } catch (e) { console.error('[journey] page images failed', e) }
  }
  try { pdf && pdf.destroy() } catch { /* noop */ }
  return { url: up.publicUrl, path: up.path, name: file.name, size: file.size, pages: pageCount || null, aspect, ...(images ? { images } : {}) }
}
/** Page images for a PDF document uploaded without them. Returns the new data.pdf object. */
export async function addPageImagesToHubDocument(journeyId, pdfObj, onProgress) {
  const pdf = await openPdf({ url: pdfObj.url })
  try {
    const up = await adminPost('documentUploadUrl', { journeyId, pageCount: pdf.numPages })
    const images = await drawAndUploadPages(pdf, up.pages || [], onProgress)
    if (!images) throw new Error('The page images did not finish.')
    return { ...pdfObj, pages: pdf.numPages, aspect: pdfObj.aspect || await pageShape(pdf), images }
  } finally { try { pdf.destroy() } catch { /* noop */ } }
}

/** Delete every journey entry tied to a milestone (used when a milestone is unposted). */
export function deleteJourneyEntriesForMilestone(projectId, milestoneId) {
  return adminPost('deleteEntriesForMilestone', { projectId, milestoneId })
}

/** Toggle a journey active/disabled. */
export function setStatus(journeyId, status) {
  return adminPost('setStatus', { journeyId, status })
}

/**
 * Rotate a journey's access credential — a fresh token AND a fresh 6-digit PIN, reactivating in
 * one write. Invalidates the OLD link+PIN for everyone who had them (the journey is scoped to
 * the project, not any specific contact — there's no way to revoke just one recipient). Returns
 * { journey, link }.
 */
export function regenerateToken(journeyId) {
  return adminPost('regenerateToken', { journeyId })
}

/**
 * Email the CURRENT journey link + PIN to a client — the standard way to hand out (or re-issue,
 * right after regenerateToken) access. Always sent from the journey's verified sender identity.
 */
export function sendJourneyAccessEmail(journeyId, toEmail, toName) {
  return adminPost('sendAccessEmail', { journeyId, toEmail, toName })
}

/** Get all responses for a journey (team review of client actions). */
export function listResponses(journeyId) {
  return adminPost('listResponses', { journeyId })
}

/** List everyone who has subscribed to email updates for this journey (active + unsubscribed). */
export function listJourneySubscribers(journeyId) {
  return adminPost('listSubscribers', { journeyId })
}

/** The Client Hub's Users branch: everyone with a link (+ email-only subscribers), each with sign-in,
 *  email-update and Getting started tour status. Returns { users }. */
export function listJourneyUsers(journeyId) {
  return adminPost('listUsers', { journeyId })
}

/**
 * Team preview — the client payload, { project, entries, responses }. Pass includeDrafts: false
 * to see exactly what the client's own link currently shows (no unpublished entries), without
 * having to open it. Defaults to true (the historical "draft view", includes unpublished entries).
 */
export function previewJourney(journeyId, { includeDrafts = true } = {}) {
  return adminPost('previewJourney', { journeyId, includeDrafts })
}

/**
 * A preview pass for one ACTIVE recipient of this journey: { token: 'pv1.…', recipient: { id, name,
 * email } }. Use the pass wherever the client would use their own token (JourneyView, the
 * inspiration tab); reads come back exactly as that person sees them, writes stay in the page.
 */
export function previewAsRecipient(journeyId, recipientId) {
  return adminPost('previewAsRecipient', { journeyId, recipientId })
}

/* ── Per-recipient access (2026-07) — the model every new send uses; regenerateToken/
   sendJourneyAccessEmail above remain only for a journey's legacy shared credential. ──────────── */

/** Every recipient on this journey, active + revoked. */
export function listRecipients(journeyId) {
  return adminPost('listRecipients', { journeyId }).then(d => d.recipients || [])
}

/** Add one or more recipients — each gets their own token+PIN and is emailed immediately.
 * recipients: [{ name, email, contactId? }] — contactId links the row to the real Contact
 * record it represents (database/137_journey_recipients_contact_link.sql), when picked from the
 * client org's Contacts rather than typed in by hand. An email already active on the journey is
 * left as-is rather than issuing a duplicate credential. */
export function addRecipients(journeyId, recipients) {
  return adminPost('addRecipients', { journeyId, recipients }).then(d => d.recipients || [])
}

/** Revoke/reactivate one recipient's access — never touches anyone else's on the same journey. */
/** Client ⇄ owner's rep (database/355, docs/CLIENT_HUB_OWNERS_REP.md). */
export function setRecipientRole(recipientId, role) {
  return adminPost('setRecipientRole', { recipientId, role }).then(d => d.recipient)
}

export function revokeRecipient(recipientId) {
  return adminPost('revokeRecipient', { recipientId }).then(d => d.recipient)
}
export function reactivateRecipient(recipientId) {
  return adminPost('reactivateRecipient', { recipientId }).then(d => d.recipient)
}

/** Permanently remove a recipient row (e.g. added by mistake) — their past responses/signature
 * events are preserved, just no longer attributed to a live row. */
export function deleteRecipient(recipientId) {
  return adminPost('deleteRecipient', { recipientId })
}

/** Re-send a recipient's CURRENT link+PIN (no rotation) — e.g. they say they lost the email. */
export function resendRecipientAccess(recipientId) {
  return adminPost('resendRecipientAccess', { recipientId })
}

/** Rotate ONE recipient's token+PIN (reactivating if revoked) and re-send — the actual answer to
 * "the client on this deal changed": regenerate just that person, add the new one separately. */
export function regenerateRecipientAccess(recipientId) {
  return adminPost('regenerateRecipientAccess', { recipientId }).then(d => d.recipient)
}

/**
 * Public, client-facing: request a fresh PIN be emailed to THIS recipient's own on-file address
 * (never one supplied here — that's what keeps this safe). Only available for a per-recipient
 * token; throws with .code = 'reset_unavailable' for a legacy shared link (nothing to email a
 * reset to), or .code = 'too_soon' if throttled (one per minute per recipient).
 */
export async function requestPinReset(token) {
  if (isPreviewToken(token)) return { ok: true, preview: true }
  const r = await fetch('/api/journey-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: token, action: 'requestReset' }),
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) {
    const e = new Error(j.error || ('Reset request failed: ' + r.status))
    e.code = j.error || 'error'
    throw e
  }
  return j
}

/**
 * A consultant's proposal on the Client Hub (docs/TEAM.md "Proposals that file themselves"):
 * payload.action 'approve' | 'decline' (note?) | 'signed' (via 'upload' with path + filename, or
 * 'direct'). Throws with the server's reason. A preview sends nothing.
 */
export async function consultantProposalAct(token, entryId, payload) {
  if (isPreviewToken(token)) return { ok: true, preview: true }
  const r = await fetch('/api/journey-respond', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
    body: JSON.stringify({ t: token, entryId, kind: 'consultant_proposal', payload, pinProof: getCachedPinProof(token) }),
  })
  const d = await r.json().catch(() => ({}))
  if (!r.ok) { const e = new Error(d.error || ('Failed: ' + r.status)); e.code = d.error; throw e }
  return d
}

/** Upload the client's signed copy of a consultant's proposal, then record it as signed. */
export async function uploadConsultantSignedCopy(token, entryId, proposalId, file) {
  if (isPreviewToken(token)) return { ok: true, preview: true }
  const { signedUrl, path } = await consultantProposalAct(token, entryId, { action: 'uploadUrl', proposalId, filename: file.name })
  const put = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/pdf' }, body: file })
  if (!put.ok) throw new Error('The upload did not go through. Please try again.')
  return consultantProposalAct(token, entryId, { action: 'signed', proposalId, via: 'upload', path, filename: file.name })
}

/** A signed neighbor letter back from the client (api/_neighborLetters.js): upload, then record it. */
export async function returnNeighborLetter(token, entryId, file, details) {
  if (isPreviewToken(token)) return { ok: true, preview: true, receivedOn: new Date().toISOString().slice(0, 10) }
  const act = async (payload) => {
    const r = await fetch('/api/journey-respond', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader(token)) },
      body: JSON.stringify({ t: token, entryId, kind: 'neighbor_letter', payload, pinProof: getCachedPinProof(token) }),
    })
    const d = await r.json().catch(() => ({}))
    if (!r.ok) { const e = new Error(d.error || ('Failed: ' + r.status)); e.code = d.error; throw e }
    return d
  }
  const { signedUrl, path } = await act({ action: 'uploadUrl', filename: file.name })
  const put = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/pdf' }, body: file })
  if (!put.ok) throw new Error('The upload did not go through. Please try again.')
  return act({ action: 'returned', path, filename: file.name, ...details })
}
